let gaInitialized = false;

/** Deduplicate Strict Mode double-effects without blocking real revisits later. */
let lastPageViewKey: string | null = null;
let lastPageViewAt = 0;
const PAGE_VIEW_DEDUPE_MS = 50;

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: Gtag;
  }
}

/** Google's gtag queue function — must push `arguments`, not a rest-params array. */
type Gtag = {
  (command: "js", date: Date): void;
  (command: "config", targetId: string, config?: Record<string, unknown>): void;
  (command: "event", eventName: string, params?: Record<string, unknown>): void;
  (command: "set", fieldName: string, value: unknown): void;
  (command: string, ...args: unknown[]): void;
};

export type GaPageViewParams = {
  page_title: string;
  page_location: string;
  page_path: string;
};

function measurementId(): string {
  return import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() ?? "";
}

function pageLocation(): string {
  if (typeof window === "undefined") return "";
  return window.location.href;
}

function installGtagStub() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js expects the raw `arguments` object in the queue, not Array.from(arguments).
    window.dataLayer!.push(arguments);
  } as Gtag;
}

function sendEvent(eventName: string, params?: Record<string, unknown>) {
  if (!measurementId()) return;
  window.gtag?.("event", eventName, params);
}

/**
 * Load GA4 when VITE_GA_MEASUREMENT_ID is set (e.g. G-XXXXXXXXXX).
 * SPA page views are sent manually via gaPageView — never by gtag config.
 */
export function initGa4() {
  const id = measurementId();
  if (!id || gaInitialized) return;
  gaInitialized = true;

  installGtagStub();

  const isDebugMode = import.meta.env.DEV;

  window.gtag!("js", new Date());
  window.gtag!("config", id, {
    send_page_view: false,
    ...(isDebugMode ? { debug_mode: true } : {}),
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  script.onerror = () => {
    console.warn(
      "[GA4] gtag.js failed to load. Disable ad blockers and confirm Network allows googletagmanager.com.",
    );
  };
  document.head.appendChild(script);
}

/** GA4 page_view for initial load and every SPA route/query change. */
export function gaPageView(params: GaPageViewParams) {
  const { page_title, page_location, page_path } = params;
  const now = Date.now();
  if (
    lastPageViewKey === page_path &&
    now - lastPageViewAt < PAGE_VIEW_DEDUPE_MS
  ) {
    return;
  }
  lastPageViewKey = page_path;
  lastPageViewAt = now;

  sendEvent("page_view", {
    page_title,
    page_location,
    page_path,
  });
}

/** @internal test helper — resets page_view dedupe state. */
export function __resetGaPageViewDedupeForTests() {
  lastPageViewKey = null;
  lastPageViewAt = 0;
}

/** Meta WaitlistClick → GA4 waitlist_click */
export function gaWaitlistClick() {
  sendEvent("waitlist_click", { page_location: pageLocation() });
}

/** Meta Lead → GA4 waitlist_submit */
export function gaWaitlistSubmit() {
  sendEvent("waitlist_submit", { page_location: pageLocation() });
}

/** Meta PrivateEventsView → GA4 private_events_view */
export function gaPrivateEventsView() {
  sendEvent("private_events_view");
}

/** Meta ViewContent → GA4 show_view */
export function gaShowView(showName: string) {
  sendEvent("show_view", { show_name: showName });
}

/** Meta WorkWithUsClick → GA4 work_with_us_click */
export function gaWorkWithUsClick() {
  sendEvent("work_with_us_click", { page_location: pageLocation() });
}

export type GaPurchaseItem = {
  item_name: string;
  quantity: number;
  price?: number;
};

export type GaPurchaseInput = {
  value: number;
  currency?: string;
  transaction_id?: string;
  items?: GaPurchaseItem[];
};

export type GaOrderItemInput = {
  title?: string;
  show_title?: string;
  quantity: number;
  amount?: number;
};

function showTitleFromItem(item: GaOrderItemInput): string {
  return item.title?.trim() || item.show_title?.trim() || "Comedy Show";
}

export function buildGaPurchaseItems(
  items: GaOrderItemInput[] | undefined,
): GaPurchaseItem[] {
  return (items ?? []).map((item) => {
    const quantity = item.quantity || 1;
    const lineTotal = item.amount ?? 0;
    const price = quantity > 0 && lineTotal > 0 ? lineTotal / quantity : undefined;
    return {
      item_name: showTitleFromItem(item),
      quantity,
      ...(price != null ? { price } : {}),
    };
  });
}

/** Meta Purchase → GA4 purchase */
export function gaPurchase(params: GaPurchaseInput) {
  sendEvent("purchase", {
    currency: params.currency ?? "USD",
    value: params.value,
    transaction_id: params.transaction_id,
    ...(params.items && params.items.length > 0 ? { items: params.items } : {}),
  });
}
